import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { 
  Integration, 
  Integration_Types, 
  CreateIntegrationDto, 
  UpdateIntegrationDto,
  IntegrationConfiguration 
} from 'app/models/integration.models';
import { IntegrationService } from 'app/services/integration.service';
import { ArchivosService } from 'app/services/archivos.service';
import { UploadImage } from 'app/interfaces/upload-images.interface';

@Component({
  selector: 'app-create-edit-integration',
  templateUrl: './create-edit-integration.component.html',
  styleUrls: ['./create-edit-integration.component.css']
})
export class CreateEditIntegrationComponent implements OnInit {
  
  integrationForm: FormGroup;
  isEditMode = false;
  integrationId?: number;
  saving = false;
  testing = false;
  jsonError = '';
  displayTags: string[] = [];
  
  // Control de visibilidad de campos password
  showToken = false;
  showApiKey = false;
  
  // Control de subida de imágenes
  uploading = false;
  
  integrationTypes = [
    { value: Integration_Types.PAYMENT_GATEWAY, label: 'Pasarela de Pago' },
    { value: Integration_Types.PAYMENT, label: 'Sistema de Pago' },
    { value: Integration_Types.SHIPPING, label: 'Envíos y Logística' },
    { value: Integration_Types.INVENTORY, label: 'Gestión de Inventario' },
    { value: Integration_Types.ACCOUNTING, label: 'Contabilidad' },
    { value: Integration_Types.CRM, label: 'CRM - Gestión de Clientes' },
    { value: Integration_Types.EMAIL, label: 'Email Marketing' },
    { value: Integration_Types.SMS, label: 'SMS y Mensajería' },
    { value: Integration_Types.SOCIAL_MEDIA, label: 'Redes Sociales' },
    { value: Integration_Types.ANALYTICS, label: 'Analytics y Métricas' },
    { value: Integration_Types.WEBHOOK, label: 'Webhook Personalizado' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private integrationService: IntegrationService,
    private archivosService: ArchivosService,
    private snackBar: MatSnackBar
  ) {
    this.integrationForm = this.createForm();
    this.setupFormListeners();
  }

  ngOnInit(): void {
    this.checkEditMode();
  }

  /**
   * Crea el formulario reactivo simplificado
   */
  private createForm(): FormGroup {
    return this.fb.group({
      // Campos esenciales
      name: ['', [Validators.required, Validators.maxLength(100)]],
      type: ['', Validators.required],
      description: ['', Validators.maxLength(500)],
      urlLogo: ['', Validators.pattern('^https?:\\/\\/.+')], // Validación URL
      
      // Credenciales de autenticación
      token: [''],
      publicKey: [''],
      
      // Configuración básica
      apiUrl: [''], // Cambiar de baseUrl a apiUrl
      authValue: ['']
    });
  }

  /**
   * Determina si se necesita mostrar la configuración adicional
   */
  needsConfiguration(): boolean {
    const selectedType = this.integrationForm.get('type')?.value;
    return selectedType && ['PAYMENT_GATEWAY', 'SHIPPING', 'EMAIL', 'CRM'].includes(selectedType);
  }

  /**
   * Configura los listeners del formulario
   */
  private setupFormListeners(): void {
    // Listener para validación de JSON
    this.integrationForm.get('configurationJson')?.valueChanges.subscribe(value => {
      this.validateJson(value);
    });
    
    // Listener para tags
    this.integrationForm.get('tagsInput')?.valueChanges.subscribe(value => {
      this.updateTagsDisplay(value);
    });
  }

  /**
   * Verifica si está en modo edición y carga los datos
   */
  private checkEditMode(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'create') {
      this.isEditMode = true;
      this.integrationId = parseInt(id, 10);
      this.loadIntegration();
      
      // Actualizar sidebar title
      const sidebar = <HTMLElement>document.querySelector(".navbar-brand");
      if (sidebar !== undefined) {
        sidebar.innerHTML = "Editar Integración";
      }
    } else {
      // Actualizar sidebar title
      const sidebar = <HTMLElement>document.querySelector(".navbar-brand");
      if (sidebar !== undefined) {
        sidebar.innerHTML = "Nueva Integración";
      }
    }
  }

  /**
   * Carga los datos de la integración para edición
   */
  private loadIntegration(): void {
    if (!this.integrationId) return;
    
    this.integrationService.getIntegrationById(this.integrationId).subscribe(
      (integration: Integration) => {
        this.populateForm(integration);
      },
      (error) => {
        console.error('Error loading integration:', error);
        this.showMessage('Error al cargar la integración', 'error');
        this.router.navigate(['/configurar/integraciones']);
      }
    );
  }

  /**
   * Llena el formulario con los datos de la integración
   */
  private populateForm(integration: Integration): void {
    // Convertir configuración a JSON string
    const configJson = integration.configuration ? 
      JSON.stringify(integration.configuration, null, 2) : '{}';
    
    // Convertir tags array a string
    const tagsString = integration.tags ? integration.tags.join(', ') : '';
    
    this.integrationForm.patchValue({
      name: integration.name,
      type: integration.type,
      description: integration.description || '',
      urlLogo: integration.urlLogo || integration.logoUrl || '',
      apiUrl: integration.apiUrl || '',
      token: integration.token || '',
      publicKey: integration.publicKey || '',
      authValue: integration.apiKey || ''
    });
    
    this.displayTags = integration.tags || [];
  }

  /**
   * Valida el JSON de configuración
   */
  private validateJson(jsonString: string): void {
    this.jsonError = '';
    
    if (!jsonString.trim()) {
      return;
    }
    
    try {
      JSON.parse(jsonString);
    } catch (error) {
      this.jsonError = 'JSON inválido: ' + (error as Error).message;
    }
  }

  /**
   * Actualiza la visualización de tags
   */
  private updateTagsDisplay(tagsString: string): void {
    if (!tagsString) {
      this.displayTags = [];
      return;
    }
    
    this.displayTags = tagsString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
  }

  /**
   * Elimina un tag
   */
  removeTag(tagToRemove: string): void {
    this.displayTags = this.displayTags.filter(tag => tag !== tagToRemove);
    const tagsString = this.displayTags.join(', ');
    this.integrationForm.patchValue({ tagsInput: tagsString });
  }

  /**
   * Verifica si un campo es inválido
   */
  isFieldInvalid(fieldName: string): boolean {
    const field = this.integrationForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  /**
   * Verifica si se puede probar la conexión
   */
  canTestConnection(): boolean {
    const apiUrl = this.integrationForm.get('apiUrl')?.value;
    const apiKey = this.integrationForm.get('apiKey')?.value;
    const token = this.integrationForm.get('token')?.value;
    
    return !!(apiUrl && (apiKey || token));
  }

  /**
   * Prueba la conexión de la integración
   */
  testConnection(): void {
    if (!this.canTestConnection() || !this.integrationId) {
      this.showMessage('Configuración insuficiente para probar conexión', 'warning');
      return;
    }
    
    this.testing = true;
    
    this.integrationService.testIntegrationConnection(this.integrationId).subscribe(
      (result) => {
        this.testing = false;
        if (result.success) {
          const message = `✓ Conexión exitosa${result.responseTime ? ` (${result.responseTime}ms)` : ''}`;
          this.showMessage(message, 'success');
        } else {
          this.showMessage(`✗ Error: ${result.message}`, 'error');
        }
      },
      (error) => {
        this.testing = false;
        console.error('Error testing connection:', error);
        this.showMessage('Error al probar la conexión', 'error');
      }
    );
  }

  /**
   * Envía el formulario
   */
  onSubmit(): void {
    if (this.integrationForm.valid) {
      this.saving = true;
      
      const operation = this.isEditMode
        ? this.integrationService.updateIntegration(this.integrationId!, this.prepareUpdateData())
        : this.integrationService.createIntegration(this.prepareCreateData());
      
      operation.subscribe(
        (response) => {
          this.saving = false;
          const message = this.isEditMode 
            ? 'Integración actualizada exitosamente'
            : 'Integración creada exitosamente';
          this.showMessage(message, 'success');
          this.router.navigate(['/configurar/integraciones']);
        },
        (error) => {
          this.saving = false;
          console.error('Error saving integration:', error);
          let errorMsg = 'Error al guardar la integración';
          if (error.error?.message) {
            errorMsg += ': ' + error.error.message;
          }
          this.showMessage(errorMsg, 'error');
        }
      );
    } else {
      this.markFormGroupTouched();
      this.showMessage('Por favor completa los campos obligatorios (nombre y tipo)', 'warning');
    }
  }

  /**
   * Prepara los datos del formulario simplificado para crear
   */
  private prepareCreateData(): CreateIntegrationDto {
    const formValue = this.integrationForm.value;
    
    return {
      name: formValue.name,
      type: formValue.type,
      description: formValue.description || '',
      urlLogo: formValue.urlLogo || '',
      apiUrl: formValue.apiUrl || '',
      token: formValue.token || '',
      publicKey: formValue.publicKey || '',
      apiKey: formValue.authValue || '',
      isActive: true, // Por defecto activa
      configuration: this.buildConfiguration(formValue)
    };
  }

  /**
   * Prepara los datos del formulario simplificado para actualizar
   */
  private prepareUpdateData(): UpdateIntegrationDto {
    const formValue = this.integrationForm.value;
    
    return {
      name: formValue.name,
      type: formValue.type,
      description: formValue.description || '',
      urlLogo: formValue.urlLogo || '',
      apiUrl: formValue.apiUrl || '',
      token: formValue.token || '',
      publicKey: formValue.publicKey || '',
      apiKey: formValue.authValue || '',
      isActive: true, // Mantener activa
      configuration: this.buildConfiguration(formValue)
    };
  }

  /**
   * Construye la configuración basada en los campos del formulario
   */
  private buildConfiguration(formValue: any): IntegrationConfiguration {
    const config: IntegrationConfiguration = {};

    // Agregar campos de configuración adicional si es necesario
    // Por ahora, los campos principales van en el objeto principal
    return config;
  }

  /**
   * Crea una nueva integración
   */
  private createIntegration(): void {
    const formValue = this.integrationForm.value;
    
    const createDto: CreateIntegrationDto = {
      name: formValue.name,
      type: formValue.type,
      description: formValue.description || undefined,
      category: formValue.category || undefined,
      
      apiUrl: formValue.apiUrl || undefined,
      webhookUrl: formValue.webhookUrl || undefined,
      apiKey: formValue.apiKey || undefined,
      token: formValue.token || undefined,
      
      timeoutSeconds: formValue.timeoutSeconds || undefined,
      rateLimitPerMinute: formValue.rateLimitPerMinute || undefined,
      rateLimitPerHour: formValue.rateLimitPerHour || undefined,
      documentationUrl: formValue.documentationUrl || undefined,
      logoUrl: formValue.logoUrl || undefined,
      
      configuration: this.parseConfigurationJson(formValue.configurationJson),
      
      supportUrl: formValue.supportUrl || undefined,
      supportEmail: formValue.supportEmail || undefined,
      tags: this.displayTags.length > 0 ? this.displayTags : undefined,
      notes: formValue.notes || undefined,
      
      isActive: formValue.isActive
    };
    
    this.integrationService.createIntegration(createDto).subscribe(
      (integration: Integration) => {
        this.saving = false;
        this.showMessage('Integración creada exitosamente', 'success');
        this.router.navigate(['/configurar/integraciones']);
      },
      (error) => {
        this.saving = false;
        console.error('Error creating integration:', error);
        this.showMessage('Error al crear la integración', 'error');
      }
    );
  }

  /**
   * Actualiza la integración existente
   */
  private updateIntegration(): void {
    if (!this.integrationId) return;
    
    const formValue = this.integrationForm.value;
    
    const updateDto: UpdateIntegrationDto = {
      name: formValue.name,
      type: formValue.type,
      description: formValue.description || undefined,
      category: formValue.category || undefined,
      
      apiUrl: formValue.apiUrl || undefined,
      webhookUrl: formValue.webhookUrl || undefined,
      apiKey: formValue.apiKey || undefined,
      token: formValue.token || undefined,
      
      timeoutSeconds: formValue.timeoutSeconds || undefined,
      rateLimitPerMinute: formValue.rateLimitPerMinute || undefined,
      rateLimitPerHour: formValue.rateLimitPerHour || undefined,
      documentationUrl: formValue.documentationUrl || undefined,
      logoUrl: formValue.logoUrl || undefined,
      
      configuration: this.parseConfigurationJson(formValue.configurationJson),
      
      supportUrl: formValue.supportUrl || undefined,
      supportEmail: formValue.supportEmail || undefined,
      tags: this.displayTags.length > 0 ? this.displayTags : undefined,
      notes: formValue.notes || undefined,
      
      isActive: formValue.isActive
    };
    
    this.integrationService.updateIntegration(this.integrationId, updateDto).subscribe(
      (integration: Integration) => {
        this.saving = false;
        this.showMessage('Integración actualizada exitosamente', 'success');
        this.router.navigate(['/configurar/integraciones']);
      },
      (error) => {
        this.saving = false;
        console.error('Error updating integration:', error);
        this.showMessage('Error al actualizar la integración', 'error');
      }
    );
  }

  /**
   * Parsea la configuración JSON
   */
  private parseConfigurationJson(jsonString: string): IntegrationConfiguration | undefined {
    if (!jsonString.trim()) {
      return undefined;
    }
    
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      return undefined;
    }
  }

  /**
   * Cancela la operación
   */
  onCancel(): void {
    if (this.integrationForm.dirty) {
      if (confirm('¿Estás seguro de que deseas cancelar? Los cambios no guardados se perderán.')) {
        this.router.navigate(['/configurar/integraciones']);
      }
    } else {
      this.router.navigate(['/configurar/integraciones']);
    }
  }

  /**
   * Marca todos los campos como touched para mostrar errores
   */
  private markFormGroupTouched(): void {
    Object.keys(this.integrationForm.controls).forEach(key => {
      const control = this.integrationForm.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Maneja la selección de imagen para subir
   */
  onImageSelected(event: any): void {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      this.showMessage('Por favor selecciona un archivo de imagen válido', 'error');
      return;
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      this.showMessage('La imagen no debe superar los 5MB', 'error');
      return;
    }

    this.uploadImage(file);
  }

  /**
   * Sube la imagen al servidor usando el servicio real
   */
  private uploadImage(file: File): void {
    this.uploading = true;

    // Usar el servicio de archivos existente para subir la imagen
    this.archivosService.subirImagen(file)
      .then((response: any) => {
        console.log('Respuesta completa del servicio:', response);
        
        // La respuesta puede tener diferentes estructuras según el servicio
        let imageUrl: string = '';
        
        // Verificar estructura: response.data.url (estructura esperada original)
        if (response && response.data && response.data.url) {
          imageUrl = response.data.url;
        }
        // Verificar estructura: response.url (estructura directa)
        else if (response && response.url) {
          imageUrl = response.url;
        }
        // Si la respuesta es directamente la URL
        else if (typeof response === 'string' && response.startsWith('http')) {
          imageUrl = response;
        }
        
        if (imageUrl) {
          // Actualizar el formulario con la URL real del servidor
          this.integrationForm.patchValue({ urlLogo: imageUrl });
          
          this.uploading = false;
          this.showMessage(`Logo subido exitosamente: ${response.originalName || file.name}`, 'success');
          
          // Limpiar el input file
          const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
          if (fileInput) fileInput.value = '';
          
          console.log('Logo subido exitosamente:', imageUrl);
        } else {
          throw new Error('No se pudo obtener la URL de la imagen de la respuesta del servidor');
        }
      })
      .catch((error) => {
        this.uploading = false;
        console.error('Error al subir logo:', error);
        this.showMessage('Error al subir el logo. Inténtalo de nuevo.', 'error');
        
        // Limpiar el input file en caso de error
        const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      });
  }

  /**
   * Obtiene la URL del logo actual
   */
  getLogoUrl(): string {
    return this.integrationForm.get('urlLogo')?.value || '';
  }

  /**
   * Limpia el logo actual
   */
  clearLogo(): void {
    this.integrationForm.patchValue({ urlLogo: '' });
  }

  /**
   * Maneja errores de carga de imagen
   */
  onImageError(event: any): void {
    console.warn('Error cargando imagen:', event);
    // Opcional: mostrar imagen placeholder o mensaje de error
  }

  /**
   * Muestra un mensaje al usuario
   */
  private showMessage(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    const config: any = { duration: 4000 };
    
    switch (type) {
      case 'success':
        config.panelClass = ['success-snackbar'];
        break;
      case 'error':
        config.panelClass = ['error-snackbar'];
        break;
      case 'warning':
        config.panelClass = ['warning-snackbar'];
        break;
      case 'info':
        config.panelClass = ['info-snackbar'];
        break;
    }
    
    this.snackBar.open(message, 'Cerrar', config);
  }
  
  /**
   * Alterna la visibilidad de los campos password
   */
  togglePasswordVisibility(field: 'token' | 'apiKey'): void {
    if (field === 'token') {
      this.showToken = !this.showToken;
    } else if (field === 'apiKey') {
      this.showApiKey = !this.showApiKey;
    }
  }
}